import React, { useCallback, useRef, useState } from 'react';
import { ApiService } from 'services/ApiService';

interface ArtistSignUpFlowProps {
  userId: string;
  onSuccess: () => void;
  onBack: () => void;
}

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const ArtistSignUpFlow: React.FC<ArtistSignUpFlowProps> = ({ userId, onSuccess, onBack }) => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [instagramUsername, setInstagramUsername] = useState('');
  const [biography, setBiography] = useState('');
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const validateAndSetFile = useCallback((file: File | null) => {
    setError(null);
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Please select a JPEG, PNG, or WebP image.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be under 5MB.');
      return;
    }

    setProfilePicture(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    validateAndSetFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    validateAndSetFile(file);
  };

  const removeFile = () => {
    setProfilePicture(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!userName.trim()) {
      setError('Username is required.');
      return;
    }
    if (!password.trim()) {
      setError('Password is required.');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('Id', userId);
      formData.append('UserName', userName.trim());
      formData.append('Password', password);

      if (instagramUsername.trim()) {
        formData.append('InstagramUsername', instagramUsername.trim());
      }
      if (biography.trim()) {
        formData.append('Biography', biography.trim());
      }
      if (profilePicture) {
        formData.append('ProfilePicture', profilePicture);
      }

      const response = await ApiService.signUpArtistMultipart(formData);

      if (response.success) {
        onSuccess();
      } else {
        const apiErrors = response.errors
          ?.map((e: { message: string }) => e.message)
          .join(', ');
        setError(apiErrors || 'Artist sign up failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Artist sign up error:', err);
      setError(err?.response?.data?.errors?.[0]?.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-6 pb-8 pt-2 sm:px-10 sm:pb-10">
      {/* Back button */}
      <button
        type="button"
        onClick={onBack}
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      <h2 className="text-xl font-bold text-gray-900">Tell us about yourself</h2>
      <p className="mt-1 text-sm text-gray-500">Set up your artist profile to get started.</p>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        {/* Username */}
        <div>
          <label htmlFor="artist-username" className="block text-sm font-medium text-gray-700">
            Username <span className="text-red-500">*</span>
          </label>
          <input
            id="artist-username"
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            required
            placeholder="Your artist username"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition-colors"
          />
        </div>

        {/* Instagram Username */}
        <div>
          <label htmlFor="artist-instagram" className="block text-sm font-medium text-gray-700">
            Instagram Username
          </label>
          <div className="mt-1 flex rounded-lg shadow-sm">
            <span className="inline-flex items-center rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
              @
            </span>
            <input
              id="artist-instagram"
              type="text"
              value={instagramUsername}
              onChange={(e) => setInstagramUsername(e.target.value)}
              placeholder="username"
              className="block w-full rounded-r-lg border border-gray-300 px-4 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition-colors"
            />
          </div>
        </div>

        {/* Biography */}
        <div>
          <label htmlFor="artist-bio" className="block text-sm font-medium text-gray-700">
            Bio
          </label>
          <textarea
            id="artist-bio"
            value={biography}
            onChange={(e) => setBiography(e.target.value)}
            rows={3}
            placeholder="Tell clients a little about yourself..."
            className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition-colors resize-none"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="artist-password" className="block text-sm font-medium text-gray-700">
            Password <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-gray-500">Re-enter your password to confirm your identity.</p>
          <input
            id="artist-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Your password"
            className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-500/20 focus:outline-none transition-colors"
          />
        </div>

        {/* Profile Picture Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Profile Picture
          </label>
          <p className="text-xs text-gray-500">Optional — JPEG, PNG, or WebP, up to 5MB.</p>

          {previewUrl ? (
            <div className="mt-2 relative inline-block">
              <img
                src={previewUrl}
                alt="Profile preview"
                className="h-32 w-32 rounded-xl object-cover shadow-md border border-gray-200"
              />
              <button
                type="button"
                onClick={removeFile}
                className="absolute -right-2 -top-2 rounded-full bg-white border border-gray-300 p-1 text-gray-500 hover:text-red-500 hover:border-red-300 transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                aria-label="Remove image"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`mt-2 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 transition-colors ${
                isDragging
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
            >
              <svg
                className={`h-10 w-10 ${isDragging ? 'text-orange-500' : 'text-gray-400'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                />
              </svg>
              <p className="mt-3 text-sm font-medium text-gray-700">
                <span className="text-orange-600">Click to upload</span> or drag and drop
              </p>
              <p className="mt-1 text-xs text-gray-500">JPEG, PNG, or WebP (max 5MB)</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="mt-2 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-orange-500/25 hover:from-orange-600 hover:to-amber-600 hover:shadow-orange-500/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg
                className="mr-2 h-5 w-5 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Creating profile...
            </>
          ) : (
            'Create Artist Profile'
          )}
        </button>
      </form>
    </div>
  );
};

export default ArtistSignUpFlow;
